import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Building,
    Search,
    MoreVertical,
    Eye,
    Edit,
    Users,
    Calendar,
    ArrowLeft,
    Plus,
    ChevronRight,
    Loader2,
    AlertCircle,
    ChevronLeft,
    DollarSign,
    CheckCircle,
    XCircle,
    Clock,
    Filter,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { merchantService } from '@/services/merchantService'
import { Merchant } from '@/types'
import { formatDistanceToNow } from 'date-fns'

export default function AdminMerchantList() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const [merchants, setMerchants] = useState<Merchant[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize] = useState(20)
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [environmentFilter, setEnvironmentFilter] = useState<string>('')

    useEffect(() => {
        fetchMerchants()
    }, [currentPage, searchTerm, statusFilter, environmentFilter])

    const fetchMerchants = async () => {
        try {
            setLoading(true)
            setError(null)

            const params = {
                page: currentPage,
                page_size: pageSize,
                search: searchTerm || undefined,
                status: statusFilter || undefined,
                environment: environmentFilter || undefined,
            }

            const response = await merchantService.getMerchants(params)

            if (response.success) {
                setMerchants(response.data.results)
                setTotalCount(response.data.count)
            } else {
                setError('Failed to fetch merchants')
            }
        } catch (err) {
            setError('An error occurred while fetching merchants')
            console.error('Error fetching merchants:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (value: string) => {
        setSearchTerm(value)
        setCurrentPage(1)
    }

    const handleStatusFilter = (status: string) => {
        setStatusFilter(status)
        setCurrentPage(1)
    }

    const handleEnvironmentFilter = (environment: string) => {
        setEnvironmentFilter(environment)
        setCurrentPage(1)
    }

    const clearFilters = () => {
        setSearchTerm('')
        setStatusFilter('')
        setEnvironmentFilter('')
        setCurrentPage(1)
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'enabled':
                return 'text-green-600 bg-green-50 border-green-200'
            case 'disabled':
                return 'text-gray-600 bg-gray-50 border-gray-200'
            case 'pending':
                return 'text-yellow-600 bg-yellow-50 border-yellow-200'
            case 'suspended':
                return 'text-red-600 bg-red-50 border-red-200'
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'enabled':
                return CheckCircle
            case 'disabled':
                return XCircle
            case 'pending':
                return Clock
            case 'suspended':
                return XCircle
            default:
                return Clock
        }
    }



    const totalPages = Math.ceil(totalCount / pageSize)

    if (loading && merchants.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen" style={pageStyles.container}>
                <div className="flex items-center space-x-2">
                    <Loader2 className="h-6 w-6 animate-spin" style={{ color: branding.primaryColor }} />
                    <span style={pageStyles.textSecondary}>Loading merchants...</span>
                </div>
            </div>
        )
    }

    return (
        <div
            className="min-h-screen"
            style={pageStyles.container}
        >
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate('/dashboard')}
                                className="hover:opacity-80"
                                style={{ color: branding.textSecondaryColor }}
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div>
                                <h1
                                    className="text-3xl font-bold"
                                    style={pageStyles.text}
                                >
                                    Merchant Management
                                </h1>
                                <p
                                    className="mt-1"
                                    style={pageStyles.textSecondary}
                                >
                                    Manage all merchants on the platform
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate('/admin/merchants/create')}
                            className="flex items-center"
                            style={{
                                backgroundColor: branding.primaryColor,
                                color: branding.textColor
                            }}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Merchant
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div
                        className="rounded-lg shadow-sm p-6"
                        style={cardStyles}
                    >
                        <div className="flex items-center">
                            <div
                                className="p-2 rounded-lg"
                                style={{
                                    backgroundColor: `${branding.primaryColor}20`
                                }}
                            >
                                <Building
                                    className="h-6 w-6"
                                    style={{ color: branding.primaryColor }}
                                />
                            </div>
                            <div className="ml-4">
                                <p
                                    className="text-sm font-medium"
                                    style={pageStyles.textSecondary}
                                >
                                    Total Merchants
                                </p>
                                <p
                                    className="text-2xl font-bold"
                                    style={pageStyles.text}
                                >
                                    {totalCount}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        className="rounded-lg shadow-sm p-6"
                        style={cardStyles}
                    >
                        <div className="flex items-center">
                            <div
                                className="p-2 rounded-lg"
                                style={{
                                    backgroundColor: statusColors.success.backgroundColor
                                }}
                            >
                                <CheckCircle
                                    className="h-6 w-6"
                                    style={{ color: statusColors.success.color }}
                                />
                            </div>
                            <div className="ml-4">
                                <p
                                    className="text-sm font-medium"
                                    style={pageStyles.textSecondary}
                                >
                                    Active
                                </p>
                                <p
                                    className="text-2xl font-bold"
                                    style={pageStyles.text}
                                >
                                    {merchants.filter(m => m.status === 'enabled').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        className="rounded-lg shadow-sm p-6"
                        style={cardStyles}
                    >
                        <div className="flex items-center">
                            <div
                                className="p-2 rounded-lg"
                                style={{
                                    backgroundColor: statusColors.warning.backgroundColor
                                }}
                            >
                                <Clock
                                    className="h-6 w-6"
                                    style={{ color: statusColors.warning.color }}
                                />
                            </div>
                            <div className="ml-4">
                                <p
                                    className="text-sm font-medium"
                                    style={pageStyles.textSecondary}
                                >
                                    Pending
                                </p>
                                <p
                                    className="text-2xl font-bold"
                                    style={pageStyles.text}
                                >
                                    {merchants.filter(m => m.status === 'pending').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        className="rounded-lg shadow-sm p-6"
                        style={cardStyles}
                    >
                        <div className="flex items-center">
                            <div
                                className="p-2 rounded-lg"
                                style={{
                                    backgroundColor: statusColors.error.backgroundColor
                                }}
                            >
                                <XCircle
                                    className="h-6 w-6"
                                    style={{ color: statusColors.error.color }}
                                />
                            </div>
                            <div className="ml-4">
                                <p
                                    className="text-sm font-medium"
                                    style={pageStyles.textSecondary}
                                >
                                    Suspended
                                </p>
                                <p
                                    className="text-2xl font-bold"
                                    style={pageStyles.text}
                                >
                                    {merchants.filter(m => m.status === 'suspended').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="rounded-lg shadow-sm p-6 mb-6"
                    style={cardStyles}
                >
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4"
                                    style={{ color: branding.textSecondaryColor }}
                                />
                                <Input
                                    placeholder="Search merchants by name, email, or company..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="pl-10"
                                    style={{
                                        backgroundColor: `${branding.surfaceColor}80`,
                                        borderColor: `${branding.textSecondaryColor}20`,
                                        color: branding.textColor,
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => handleStatusFilter(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-300"
                                style={{
                                    backgroundColor: `${branding.surfaceColor}80`,
                                    borderColor: `${branding.textSecondaryColor}20`,
                                    color: branding.textColor,
                                }}
                            >
                                <option value="">All Status</option>
                                <option value="enabled">Enabled</option>
                                <option value="disabled">Disabled</option>
                                <option value="pending">Pending</option>
                                <option value="suspended">Suspended</option>
                            </select>

                            <select
                                value={environmentFilter}
                                onChange={(e) => handleEnvironmentFilter(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-300"
                                style={{
                                    backgroundColor: `${branding.surfaceColor}80`,
                                    borderColor: `${branding.textSecondaryColor}20`,
                                    color: branding.textColor,
                                }}
                            >
                                <option value="">All Environments</option>
                                <option value="production">Production</option>
                                <option value="sandbox">Sandbox</option>
                            </select>

                            {(searchTerm || statusFilter || environmentFilter) && (
                                <Button
                                    variant="outline"
                                    onClick={clearFilters}
                                    className="text-gray-300"
                                >
                                    <Filter className="h-4 w-4 mr-2" />
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <div className="rounded-lg shadow-sm p-4 mb-6"
                        style={cardStyles}
                    >
                        <div className="flex items-center">
                            <AlertCircle
                                className="h-5 w-5 mr-2"
                                style={{ color: statusColors.error.color }}
                            />
                            <span style={{ color: statusColors.error.color }}>{error}</span>
                        </div>
                    </div>
                )}

                {/* Merchants Table */}
                <div className="rounded-lg shadow-sm overflow-hidden"
                    style={cardStyles}
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-700"
                                style={{
                                    backgroundColor: `${branding.surfaceColor}80`,
                                    borderColor: `${branding.textSecondaryColor}20`,
                                    color: branding.textColor,
                                }}
                            >
                                <tr>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                                        style={pageStyles.textSecondary}
                                    >
                                        Merchant
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                                        style={pageStyles.textSecondary}
                                    >
                                        Status
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                                        style={pageStyles.textSecondary}
                                    >
                                        Environment
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                                        style={pageStyles.textSecondary}
                                    >
                                        Users
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                                        style={pageStyles.textSecondary}
                                    >
                                        Commission
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                                        style={pageStyles.textSecondary}
                                    >
                                        Created
                                    </th>
                                    <th
                                        className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider"
                                        style={pageStyles.textSecondary}
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-gray-800 divide-y divide-gray-200"
                                style={{
                                    backgroundColor: `${branding.surfaceColor}80`,
                                    borderColor: `${branding.textSecondaryColor}20`,
                                    color: branding.textColor,
                                }}
                            >
                                {merchants.map((merchant) => {
                                    const StatusIcon = getStatusIcon(merchant.status)
                                    return (
                                        <tr key={merchant.id} className="hover:bg-gray-700"
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}80`,
                                                borderColor: `${branding.textSecondaryColor}20`,
                                                color: branding.textColor,
                                            }}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <div
                                                            className="h-10 w-10 rounded-full flex items-center justify-center"
                                                            style={{
                                                                backgroundColor: branding.primaryColor,
                                                                color: branding.textColor,
                                                            }}
                                                        >
                                                            <Building
                                                                className="h-5 w-5"
                                                                style={{ color: branding.textColor }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium"
                                                            style={pageStyles.textSecondary}
                                                        >
                                                            {merchant.company_name}
                                                        </div>
                                                        <div className="text-sm"
                                                            style={pageStyles.textSecondary}
                                                        >{merchant.email}</div>
                                                        <div className="text-xs"
                                                            style={pageStyles.textSecondary}
                                                        >{merchant.name}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(merchant.status)}`}>
                                                    <StatusIcon
                                                        className="h-3 w-3 mr-1"
                                                        style={{ color: branding.textSecondaryColor }}
                                                    />
                                                    {merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border`}
                                                    style={{
                                                        backgroundColor: `${branding.primaryColor}20`,
                                                        borderColor: `${branding.textSecondaryColor}20`,
                                                        color: branding.textColor,
                                                    }}
                                                >
                                                    {merchant.environment ? merchant.environment.charAt(0).toUpperCase() + merchant.environment.slice(1) : 'Unknown'}
                                                </span>
                                            </td>
                                            <td
                                                className="px-6 py-4 whitespace-nowrap text-sm"
                                                style={pageStyles.textSecondary}
                                            >
                                                <div className="flex items-center">
                                                    <Users
                                                        className="h-4 w-4 mr-1"
                                                        style={{ color: branding.textSecondaryColor }}
                                                    />
                                                    {merchant.users_count}
                                                </div>
                                            </td>
                                            <td
                                                className="px-6 py-4 whitespace-nowrap text-sm"
                                                style={pageStyles.textSecondary}
                                            >
                                                <div className="flex items-center">
                                                    <DollarSign
                                                        className="h-4 w-4 mr-1"
                                                        style={{ color: branding.textSecondaryColor }}
                                                    />
                                                    {merchant.commission_rate}%
                                                </div>
                                            </td>
                                            <td
                                                className="px-6 py-4 whitespace-nowrap text-sm"
                                                style={pageStyles.textSecondary}
                                            >
                                                <div className="flex items-center">
                                                    <Calendar
                                                        className="h-4 w-4 mr-1"
                                                        style={{ color: branding.textSecondaryColor }}
                                                    />
                                                    {formatDistanceToNow(new Date(merchant.created_at), { addSuffix: true })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/merchants/${merchant.id}`)}
                                                        className="text-gray-300 hover:text-gray-900"
                                                        style={{
                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                            borderColor: `${branding.textSecondaryColor}20`,
                                                            color: branding.textColor,
                                                        }}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/merchants/${merchant.id}/edit`)}
                                                        className="text-gray-300 hover:text-gray-900"
                                                        style={{
                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                            borderColor: `${branding.textSecondaryColor}20`,
                                                            color: branding.textColor,
                                                        }}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-gray-300 hover:text-gray-900"
                                                        style={{
                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                            borderColor: `${branding.textSecondaryColor}20`,
                                                            color: branding.textColor,
                                                        }}
                                                    >
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {merchants.length === 0 && !loading && (
                        <div className="text-center py-12">
                            <Building
                                className="mx-auto h-12 w-12"
                                style={{ color: branding.textSecondaryColor }}
                            />
                            <h3
                                className="mt-2 text-sm font-medium"
                                style={pageStyles.text}
                            >
                                No merchants found
                            </h3>
                            <p
                                className="mt-1 text-sm"
                                style={pageStyles.textSecondary}
                            >
                                {searchTerm || statusFilter || environmentFilter
                                    ? 'Try adjusting your search criteria.'
                                    : 'Get started by creating a new merchant.'}
                            </p>
                            <div className="mt-6">
                                <Button
                                    onClick={() => navigate('/admin/merchants/create')}
                                    style={{
                                        backgroundColor: branding.primaryColor,
                                        color: branding.textColor
                                    }}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Create Merchant
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                        <div
                            className="text-sm"
                            style={pageStyles.textSecondary}
                        >
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} merchants
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                style={{
                                    backgroundColor: 'transparent',
                                    borderColor: branding.textSecondaryColor,
                                    color: branding.textSecondaryColor
                                }}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                            </Button>

                            <div className="flex items-center space-x-1">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    const page = i + 1
                                    return (
                                        <Button
                                            key={page}
                                            variant={currentPage === page ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setCurrentPage(page)}
                                            style={currentPage === page ? {
                                                backgroundColor: branding.primaryColor,
                                                color: branding.textColor
                                            } : {
                                                backgroundColor: 'transparent',
                                                borderColor: branding.textSecondaryColor,
                                                color: branding.textSecondaryColor
                                            }}
                                        >
                                            {page}
                                        </Button>
                                    )
                                })}
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                style={{
                                    backgroundColor: 'transparent',
                                    borderColor: branding.textSecondaryColor,
                                    color: branding.textSecondaryColor
                                }}
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
